/**
 * Cloud Provider Type Definitions
 * Phase 3: State Management Migration - Type Safety Implementation
 * 
 * Comprehensive type definitions for multi-cloud provider support
 * with configuration metadata for UI components and state management
 */

export type CloudProvider = 'aws' | 'gcp' | 'azure'

export interface CloudProviderConfig {
  id: CloudProvider
  name: string
  displayName: string
  color: string
  bgColor: string
  textColor: string
  icon: string
  description: string
}

export const CLOUD_PROVIDERS: Record<CloudProvider, CloudProviderConfig> = {
  aws: {
    id: 'aws',
    name: 'AWS',
    displayName: 'Amazon Web Services',
    color: 'text-orange-600',
    bgColor: 'bg-orange-500',
    textColor: 'text-white',
    icon: 'aws',
    description: 'Amazon Web Services cloud platform'
  },
  gcp: {
    id: 'gcp',
    name: 'GCP',
    displayName: 'Google Cloud Platform',
    color: 'text-blue-600',
    bgColor: 'bg-blue-500',
    textColor: 'text-white',
    icon: 'gcp',
    description: 'Google Cloud Platform services'
  },
  azure: {
    id: 'azure',
    name: 'Azure',
    displayName: 'Microsoft Azure',
    color: 'text-blue-700',
    bgColor: 'bg-blue-600',
    textColor: 'text-white',
    icon: 'azure',
    description: 'Microsoft Azure cloud services'
  }
}

/**
 * Type guard for validating cloud provider values
 * @param provider - String to validate as CloudProvider
 * @returns Type predicate for CloudProvider
 */
export const isValidCloudProvider = (provider: string): provider is CloudProvider => {
  return ['aws', 'gcp', 'azure'].includes(provider)
}

/**
 * Default cloud provider for initialization
 */
export const DEFAULT_CLOUD_PROVIDER: CloudProvider = 'aws'

/**
 * Cloud provider configuration retrieval utility
 * @param provider - CloudProvider to get configuration for
 * @returns CloudProviderConfig object
 */
export const getCloudProviderConfig = (provider: CloudProvider): CloudProviderConfig => {
  return CLOUD_PROVIDERS[provider]
}